import React, { useState, useMemo, useEffect } from "react";
import AddBlog from "./AddBlog";
import { useNavigate } from "react-router-dom";

export default function List() {
	const navigate = useNavigate();
	// state: server-driven pagination
	const [blogs, setBlogs] = useState([]);
	const [isDialogOpen, setDialogOpen] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [form, setForm] = useState({ heading: "", subHeading: "", content: "" });

	// page is zero-based locally to match server usage where we send page+1
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);

	// totalPages computed from server total
	const totalPages = useMemo(() => Math.max(1, Math.ceil(total / rowsPerPage)), [total, rowsPerPage]);
	// server returns only the page slice, so visibleBlogs is the server response
	const visibleBlogs = blogs;

	// fetch function using the provided pattern (uses token from localStorage)
	async function fetchBlogs(p = page, limit = rowsPerPage) {
		setLoading(true);
		const token = localStorage.getItem("token");
		try {
			const res = await fetch(`${process.env.REACT_APP_SERVER_URI}/blogs/list?page=${p + 1}&limit=${limit}`, {
				headers: {
					Authorization: token ? `Bearer ${token}` : "",
				},
			});
			const result = await res.json();
			console.log("Full API Response:", result);
			setBlogs(result.blogs || []);
			setTotal(result.total || 0);
		} catch (error) {
			console.error("Error fetching blogs:", error);
			setBlogs([]);
			setTotal(0);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		fetchBlogs(page, rowsPerPage);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, rowsPerPage]);

	function openAdd() {
		navigate("/addBlogs");
	}

	function openEdit(blog) {
		setEditingId(blog.id);
		setForm({ heading: blog.heading, subHeading: blog.subHeading, content: blog.content });
		setDialogOpen(true);
	}

	function closeDialog() {
		setDialogOpen(false);
		setEditingId(null);
	}

	// save uses backend create/update then refreshes the list
	async function save() {
		const trimmedHeading = (form.heading || "").trim();
		if (!trimmedHeading) {
			alert("Heading is required.");
			return;
		}

		const token = localStorage.getItem("token");
		try {
			if (editingId == null) {
				// create: POST to backend create endpoint (adjust path if your backend differs)
				const res = await fetch(`${process.env.REACT_APP_SERVER_URI}/blogs/add`, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: token ? `Bearer ${token}` : "",
					},
					body: JSON.stringify({ heading: form.heading, subHeading: form.subHeading, content: form.content }),
				});
				if (!res.ok) throw new Error("Create failed");
				// after creating, go to first page and refresh
				setPage(0);
				await fetchBlogs(0, rowsPerPage);
			} else {
				// update: PUT to backend update endpoint (adjust path if your backend differs)
				const res = await fetch(`${process.env.REACT_APP_SERVER_URI}/blogs/update/${editingId}`, {
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
						Authorization: token ? `Bearer ${token}` : "",
					},
					body: JSON.stringify({ heading: form.heading, subHeading: form.subHeading, content: form.content }),
				});
				if (!res.ok) throw new Error("Update failed");
				// refresh current page after update
				await fetchBlogs(page, rowsPerPage);
			}
			closeDialog();
		} catch (err) {
			console.error(err);
			alert("Failed to save blog. See console for details.");
		}
	}

	function onChange(e) {
		const { name, value } = e.target;
		setForm(prev => ({ ...prev, [name]: value }));
	}

	function goToPage(p) {
		if (p < 0 || p >= totalPages) return;
		setPage(p);
	}

	return (
		<div style={{ padding: 16, fontFamily: "Arial, sans-serif" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
				<h2 style={{ margin: 0 }}>Blogs</h2>
				<button onClick={openAdd} style={{ padding: "8px 12px", cursor: "pointer" }}>Add Blog</button>
			</div>

			{loading ? (
				<p>Loading blogs...</p>
			) : visibleBlogs.length === 0 ? (
				<p>No blogs yet.</p>
			) : (
				<table style={{ width: "100%", borderCollapse: "collapse" }}>
					<thead>
						<tr>
							<th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px" }}>Heading</th>
							<th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px" }}>Sub Heading</th>
							<th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: "8px" }}>Content</th>
							<th style={{ textAlign: "right", borderBottom: "1px solid #ddd", padding: "8px" }}>Actions</th>
						</tr>
					</thead>
					<tbody>
						{visibleBlogs.map(blog => (
							<tr key={blog.id}>
								<td style={{ padding: "8px", borderBottom: "1px solid #f0f0f0", width: "25%" }}>{blog.heading}</td>
								<td style={{ padding: "8px", borderBottom: "1px solid #f0f0f0", width: "20%" }}>{blog.subHeading}</td>
								<td style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>{blog.content}</td>
								<td style={{ padding: "8px", borderBottom: "1px solid #f0f0f0", textAlign: "right" }}>
									<a
										href={`https://thetool.in/blog/${blog.slug}`}
										target="_blank"
										rel="noopener noreferrer"
										style={{ textDecoration: "none", color: "#007bff" }}
									>
										View
									</a>
								</td>
								{/* <td style={{ padding: "8px", borderBottom: "1px solid #f0f0f0", textAlign: "right" }}>
									<button onClick={() => openEdit(blog)} style={{ marginRight: 8, padding: "6px 10px", cursor: "pointer" }}>Edit</button>
								</td> */}
							</tr>
						))}
					</tbody>
				</table>
			)}

			{total > rowsPerPage && (
				<div style={{ marginTop: 12, display: "flex", justifyContent: "center", gap: 8 }}>
					<button onClick={() => goToPage(page - 1)} disabled={page === 0} style={{ padding: "6px 10px", cursor: page === 0 ? "not-allowed" : "pointer" }}>
						Prev
					</button>
					{Array.from({ length: totalPages }, (_, i) => i).map(p => (
						<button key={p} onClick={() => goToPage(p)} style={{ padding: "6px 10px", fontWeight: p === page ? "bold" : "normal", cursor: "pointer" }}>
							{p + 1}
						</button>
					))}
					<button onClick={() => goToPage(page + 1)} disabled={page === totalPages - 1} style={{ padding: "6px 10px", cursor: page === totalPages - 1 ? "not-allowed" : "pointer" }}>
						Next
					</button>
				</div>
			)}
		</div>
	);
}